import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Check } from 'lucide-react';
import { dankeText } from '@/pages/UnterschriftStationPage';
import VertragAnzeige, { UV_STIL } from '@/components/unterschrift/VertragAnzeige';

/* Unterschrift-Station, Ausweichweg (Bauauftrag 03.10.2026): der Kunde scannt an der Theke den QR-Code
   und unterschreibt am eigenen Handy — dieselbe Ansicht, einspaltig, ohne Anmeldung. Das Einmal-Token
   steht nur in der Adresse. Ist es unbekannt, abgelaufen, verbraucht oder abgebrochen, antwortet der
   Server immer gleich; die Seite nennt deshalb auch keinen Grund. Keine Abfrage im Takt: geladen wird
   einmal, geprueft wird beim Unterschreiben. */

const BASIS = `${process.env.REACT_APP_BACKEND_URL || ''}/api/public/unterschrift`;

export default function UnterschriftQrPage() {
  const { token } = useParams();
  const [vorgang, setVorgang] = useState(null);
  const [zustand, setZustand] = useState('laden');      // laden | vertrag | danke | ungueltig | zuviel | fehler | unklar

  const [versuch, setVersuch] = useState(0);
  useEffect(() => {
    let aktiv = true;
    setZustand('laden');
    axios.get(`${BASIS}/${encodeURIComponent(token)}`)
      .then((r) => { if (aktiv) { setVorgang(r.data.vorgang); setZustand('vertrag'); } })
      // nur 404 heisst „ungueltig" — Funkloch oder Server im Neustart ist ein Ladefehler mit „Erneut versuchen"
      .catch((e) => { if (!aktiv) return; const s = e?.response?.status; setZustand(s === 404 ? 'ungueltig' : s === 429 ? 'zuviel' : 'fehler'); });
    return () => { aktiv = false; };
  }, [token, versuch]);

  const antwortVerloren = useRef(false);     // Unterschrift abgeschickt, aber keine Antwort bekommen (Funkloch)
  const absenden = async (eingabe) => {
    try {
      await axios.post(`${BASIS}/${encodeURIComponent(token)}/unterschreiben`, eingabe);
    } catch (e) {
      const s = e?.response?.status;
      if (!e?.response || s >= 500) antwortVerloren.current = true;      // auch 502/504: der Abschluss kann trotzdem gelaufen sein
      // 404 nach verlorener Antwort heisst sehr wahrscheinlich: der erste Versuch kam an und der Vertrag steht
      if (s === 404) { setVorgang(null); setZustand(antwortVerloren.current ? 'unklar' : 'ungueltig'); return false; }
      const d = e?.response?.data?.detail;
      const meldung = s === 429 ? 'Zu viele Versuche — bitte kurz warten.'
        : !e?.response ? 'Keine Verbindung — bitte noch einmal auf „Unterschreiben“ tippen.'
          : (typeof d === 'string' ? d : null);
      throw Object.assign(new Error('abgelehnt'), { meldung });
    }
    return true;
  };

  // Kontoänderung (Pia-Finanzen F2): IBAN selbst eingeben, die Antwort ist die neue Anzeige (IBAN maskiert)
  const bankSenden = async (daten) => {
    try {
      const r = await axios.post(`${BASIS}/${encodeURIComponent(token)}/bank`, daten);
      setVorgang(r.data.vorgang);
    } catch (e) {
      const s = e?.response?.status;
      if (s === 404) { setVorgang(null); setZustand('ungueltig'); return; }
      const d = e?.response?.data?.detail;
      throw Object.assign(new Error('abgelehnt'), { meldung: s === 429 ? 'Zu viele Versuche — bitte kurz warten.' : !e?.response ? 'Keine Verbindung — bitte noch einmal versuchen.' : (typeof d === 'string' ? d : null) });
    }
  };

  return (
    <div className="uv" data-testid="unterschrift-qr">
      <style>{UV_STIL}</style>
      <header className="uv-kopf">
        <img src="/logo.png" alt="P1 Performance Center" />
        <span>Vertrag unterschreiben</span>
      </header>
      {zustand === 'laden' && <div className="uv-warten"><span className="uv-puls" /></div>}
      {zustand === 'vertrag' && vorgang && (
        <VertragAnzeige vorgang={vorgang} absenden={absenden} einspaltig onFertig={() => setZustand('danke')} bankSenden={bankSenden} />
      )}
      {zustand === 'danke' && (
        <div className="uv-warten" data-testid="uv-danke">
          <div className="uv-erfolg"><Check strokeWidth={3} /></div>
          <h1 className="uv-titel">Danke — unterschrieben</h1>
          <p>{dankeText(vorgang?.art)} Diese Seite kannst du schließen.</p>
        </div>
      )}
      {zustand === 'unklar' && (
        <div className="uv-warten" data-testid="uv-unklar">
          <h1 className="uv-titel">Bitte kurz im Studio nachfragen</h1>
          <p>Die Verbindung war kurz weg. Deine Unterschrift ist sehr wahrscheinlich angekommen — das Team sieht es am Bildschirm. Bitte nicht noch einmal unterschreiben.</p>
        </div>
      )}
      {zustand === 'fehler' && (
        <div className="uv-warten" data-testid="uv-ladefehler">
          <h1 className="uv-titel">Keine Verbindung</h1>
          <p>Der Vertrag konnte gerade nicht geladen werden. Bitte noch einmal versuchen.</p>
          <button type="button" className="uv-sekundaer" onClick={() => setVersuch((n) => n + 1)}>Erneut versuchen</button>
        </div>
      )}
      {(zustand === 'ungueltig' || zustand === 'zuviel') && (
        <div className="uv-warten" data-testid="uv-ungueltig">
          <h1 className="uv-titel">{zustand === 'zuviel' ? 'Bitte kurz warten' : 'Link nicht mehr gültig'}</h1>
          <p>{zustand === 'zuviel' ? 'Es gab zu viele Aufrufe. Bitte in ein paar Minuten noch einmal versuchen.'
            : 'Dieser Link kann nicht mehr verwendet werden. Bitte im Studio einen neuen Code oder Link anfordern.'}</p>
        </div>
      )}
    </div>
  );
}
